import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import { faChevronsRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassValue, clsx } from 'clsx';

import { cn } from '../../lib/utils.ts';
import { ButtonText } from '../ButtonText';

export interface SidePanelProps {
    width?: string | number;
    position?: 'left' | 'right';
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    className?: ClassValue;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    children: ReactNode;
    titleSideElement?: ReactNode;
    expiremantalClickOutsideWithNoInert?: boolean;
    disableClickOutside?: boolean;
}

export const SidePanel = ({
    width = 300,
    isOpen,
    onClose,
    title,
    className,
    dataTestid,
    htmlAttributes,
    position = 'right',
    children,
    titleSideElement,
    disableClickOutside = false,
}: SidePanelProps) => {
    const mountPoint = document.getElementById('app-layout-sidepanel');
    const productionWarningMessage = document.getElementById('production-warning-message');

    useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event: MouseEvent) {
            if (!disableClickOutside && mountPoint && event.target && !mountPoint.contains(event.target as Node)) {
                if (isOpen) onClose();
            }
        }
        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, mountPoint, isOpen, disableClickOutside]);

    if (mountPoint) {
        return createPortal(
            <Drawer
                open={isOpen}
                size={width}
                onClose={onClose}
                data-testid={dataTestid}
                direction={position}
                enableOverlay={false}
                duration={0}
                className='z-sidepanel!'
            >
                <div
                    role='dialog'
                    className={cn(
                        `flex h-screen flex-col bg-white`,
                        productionWarningMessage ? 'mt-10' : '',
                        className
                    )}
                    data-component-name='SidePanel'
                    {...htmlAttributes}
                >
                    <div className={clsx('flex w-full items-center gap-2 bg-white px-6 pt-6 pb-4')}>
                        <ButtonText
                            type='button'
                            onClick={onClose}
                            className='absolute -left-8 flex items-center gap-2 rounded-r-none bg-white backdrop-blur-2xl'
                        >
                            <FontAwesomeIcon icon={faChevronsRight} />
                        </ButtonText>
                        <div className='mr-auto font-bold'>{title}</div>
                        {titleSideElement}
                    </div>
                    <div className='flex-1 overflow-auto px-6 pb-6'>{children}</div>
                </div>
            </Drawer>,
            mountPoint
        );
    }
};
